import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from 'react';

import {
  AddItemLarge,
  AddItemMedium,
  AddItemSmall,
  AddMedia,
} from '@wix/wix-ui-icons-common/system';

import { Add } from '@wix/wix-ui-icons-common';
import { useFocusRing } from '../providers/useFocusRing/useFocusRing';
import { useId } from '../utils/useId';
import { useIcons } from '../WixDesignSystemIconThemeProvider';

import { Tooltip } from '../Tooltip';
import { Text } from '../Text';
import { Image } from '../Image';

import { dataHooks } from './constants';

import { st, classes } from './AddItem.st.css';
import { AddItemProps, AddItemActions } from './AddItem.types';
import { deprecationLog } from '../utils/deprecationLog';

const illustrationDimensions = {
  tiny: { height: 24, width: 24 },
  small: { height: 60, width: 60 },
  medium: { height: 120, width: 120 },
  large: { height: 120, width: 120 },
} as const;

const tooltipPlacementByAlignment = {
  left: 'top-start',
  right: 'top-end',
  center: 'top',
} as const;

const AddItem = forwardRef<AddItemActions, AddItemProps>(
  (
    {
      theme = 'dashes', // TODO: add default skin
      size = 'tiny',
      alignItems = 'center',
      removePadding = false,
      borderRadius,
      disabled,
      className,
      onClick,
      ariaLabel,
      icon,
      children,
      subtitle,
      dataHook,
      tooltipProps,
      tooltipContent,
      showIcon = true,
      ...props
    },
    ref,
  ) => {
    const icons = useIcons('AddItem', {
      Add,
      AddItemLarge,
      AddItemMedium,
      AddItemSmall,
      AddMedia,
    });

    const skin = props.skin || theme;
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (theme) {
        deprecationLog(
          '<AddItem/> - prop "theme" is deprecated and will be removed in next major release, please use "skin" property instead.',
        );
      }
    }, [theme]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        buttonRef.current?.focus();
      },
      blur: () => {
        buttonRef.current?.blur();
      },
    }));

    const iconElement = useMemo(() => {
      if (!showIcon) {
        return;
      }
      if (!icon) {
        const isImageIcon = skin === 'image';

        if (isImageIcon) {
          return (
            <icons.AddMedia
              data-hook={dataHooks.defaultIcon}
              className={classes.icon}
              width="31"
              height="31"
            />
          );
        }

        if (size === 'tiny') {
          return (
            <icons.Add
              data-hook={dataHooks.defaultIcon}
              className={classes.icon}
              width="24"
              height="24"
            />
          );
        }

        if (size === 'small') {
          return <icons.AddItemSmall data-hook={dataHooks.defaultIcon} />;
        }

        if (size === 'medium') {
          return <icons.AddItemMedium data-hook={dataHooks.defaultIcon} />;
        }

        return <icons.AddItemLarge data-hook={dataHooks.defaultIcon} />;
      }
      if (typeof icon === 'string') {
        return (
          <Image
            className={classes.illustration}
            fit="contain"
            src={icon}
            {...illustrationDimensions[size]}
          />
        );
      }
      return icon;
    }, [icon, size, skin, showIcon, icons]);

    const textElement = useMemo(() => {
      if (!children || skin === 'image') {
        return;
      }
      const textSize = size === 'tiny' ? 'small' : 'medium';
      return (
        <div className={st(classes.textWrapper, { size })}>
          {typeof children === 'function' ? (
            children()
          ) : (
            <Text
              className={classes.textContent}
              weight="thin"
              skin="standard"
              size={textSize}
              dataHook={dataHooks.itemText}
              ellipsis
            >
              {children}
            </Text>
          )}
        </div>
      );
    }, [children, size, skin]);

    const subtitleElement = useMemo(() => {
      return (
        subtitle && (
          <Text
            className={st(classes.subtitle, { size })}
            size="small"
            dataHook={dataHooks.itemSubtitle}
          >
            {subtitle}
          </Text>
        )
      );
    }, [subtitle, size]);

    const commonButtonProps = {
      className,
      onClick,
      disabled,
      skin,
      size,
      ariaLabel,
      borderRadius,
      removePadding,
      alignItems,
      icon: iconElement,
      text: textElement,
      subtitle: subtitleElement,
    };

    // TODO: remove this once we deprecate content usage thhrough tooltip
    // @ts-expect-error
    const tooltip = tooltipContent || tooltipProps?.content;
    const tooltipId = useId('additem-tooltip_');

    return (
      <div style={{ width: '100%', height: '100%' }} data-hook={dataHook}>
        {tooltip && (
          <Tooltip
            content={tooltip}
            className={classes.tooltip}
            flip={skin === 'image'}
            moveBy={{ y: skin === 'image' ? -12 : 0 }}
            dataHook={dataHooks.itemTooltip}
            placement={tooltipPlacementByAlignment[alignItems]}
            aria-describedby={tooltipId}
            {...tooltipProps}
          >
            {({ onFocus, onBlur }) => (
              <AddItemButton
                ref={buttonRef}
                ariaDescribedBy={tooltipId}
                {...commonButtonProps}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            )}
          </Tooltip>
        )}
        {!tooltip && <AddItemButton ref={buttonRef} {...commonButtonProps} />}
      </div>
    );
  },
);

const AddItemButton = forwardRef<
  HTMLButtonElement,
  {
    className?: AddItemProps['className'];
    onClick: AddItemProps['onClick'];
    disabled: AddItemProps['disabled'];
    skin: AddItemProps['skin'];
    size: AddItemProps['size'];
    ariaLabel: AddItemProps['ariaLabel'];
    ariaDescribedBy?: AddItemProps['ariaDescribedBy'];
    borderRadius: AddItemProps['borderRadius'];
    removePadding: AddItemProps['removePadding'];
    alignItems: AddItemProps['alignItems'];
    icon: React.ReactNode;
    text: React.ReactNode;
    subtitle: React.ReactNode;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
  }
>(
  (
    {
      className,
      onClick,
      disabled,
      size,
      ariaLabel,
      borderRadius,
      alignItems = 'center',
      removePadding,
      skin,
      icon,
      text,
      subtitle,
      ariaDescribedBy,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing();

    return (
      <button
        ref={ref}
        data-hook={dataHooks.addItem}
        className={st(
          classes.root,
          {
            skin,
            size,
            removePadding,
            borderRadius,
            disabled,
            alignItems,
            'focus-visible': isFocusVisible,
          },
          className,
        )}
        style={{ borderRadius }}
        disabled={disabled}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        {...focusProps}
        onFocus={e => {
          focusProps.onFocus?.(e);
          onFocus?.(e);
        }}
        onBlur={e => {
          focusProps.onBlur?.(e);
          onBlur?.(e);
        }}
      >
        <div
          className={st(classes.contentContainer, {
            skin,
            size,
            alignItems,
            disabled,
          })}
        >
          <div className={st(classes.content, { size })}>
            {icon}
            {text}
          </div>
          {subtitle}
        </div>
      </button>
    );
  },
);

AddItem.displayName = 'AddItem';

export { AddItem };
